import type { QueryResult } from '@apollo/react-common';
import { statusCategoryForId } from '@atlassian/jira-common-constants/src/status-categories';
import { type IssueId, toIssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import type {
	jira_deploymentFrequencyReportDeploymentDetails_issueDetails_Query as DeploymentFrequencyReportIssueDetailsQuery,
	jira_deploymentFrequencyReportDeploymentDetails_issueDetails_QueryVariables as DeploymentFrequencyReportIssueDetailsQueryVariables,
	jira_deploymentFrequencyReportDeploymentDetails_issueDetails_Query_issues_nodes as IssueNode,
} from './__generated_apollo__/jira_deploymentFrequencyReportDeploymentDetails_issueDetails_Query';
import type { Issue, ServiceResult } from './types';

// At most 100 issues can be associated with a deployment.
// This also corresponds with the maximum page size supported by the JQL search field.
const PAGE_SIZE = 100;

const buildJql = (issueIds: IssueId[]): string =>
	issueIds.length > 0 ? `id IN (${issueIds.join(',')})` : '';

export const buildQueryVariables = (
	issueIds: IssueId[],
): DeploymentFrequencyReportIssueDetailsQueryVariables => ({
	first: PAGE_SIZE,
	jql: buildJql(issueIds),
});

const transformNode = (node?: IssueNode | null): Issue => {
	const key = node?.issuekey?.stringValue;
	const summary = node?.summary?.textValue;
	const issueTypeIconUrl = node?.issuetype?.iconUrl;
	const assigneeDisplayName = node?.assignee?.userValue?.displayName;
	const assigneeAvatarUrl = node?.assignee?.userValue?.avatarUrl;
	const statusName = node?.status?.name;
	const statusCategory = statusCategoryForId(node?.status?.statusCategoryId ?? undefined);

	if (key == null || summary == null || statusName == null || issueTypeIconUrl == null) {
		throw Error('Invalid issue node');
	}

	const assignee =
		assigneeDisplayName == null || assigneeAvatarUrl == null
			? 'unassigned'
			: {
					displayName: assigneeDisplayName,
					avatarUrl: assigneeAvatarUrl,
				};

	return {
		key: toIssueKey(key),
		summary,
		issueType: {
			iconUrl: issueTypeIconUrl,
		},
		assignee,
		status: {
			name: statusName,
			category: statusCategory,
		},
	};
};

export const transformResult = (
	queryResult: QueryResult<
		DeploymentFrequencyReportIssueDetailsQuery,
		DeploymentFrequencyReportIssueDetailsQueryVariables
	>,
): ServiceResult<Issue[]> => {
	const resultBase = {
		loading: queryResult.loading,
		error: queryResult.error,
	};

	const nodes = queryResult.data?.issues?.nodes;

	if (queryResult.loading || nodes == null) {
		return resultBase;
	}

	try {
		return {
			...resultBase,
			data: nodes.map(transformNode),
		};
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		return {
			...resultBase,
			error: queryResult.error ?? error,
		};
	}
};
